import type { Device } from '@/types/app';
import type { Component } from '@/types/component';
import type { MobileAppEnvironment, MobileAppTrackingStatus } from '@/types/mobileApp';
import type { PaymentConfig } from '@/types/payment';
import type { AppState } from 'lib/contexts/AppState';
import type { MobileAppState } from 'lib/contexts/MobileAppState';

import { useStable } from '@/hooks/useStable';
import { AppStateProvider } from 'lib/contexts/AppState';
import { ComponentsStateProvider } from 'lib/contexts/ComponentsState';
import { MobileAppStateProvider } from 'lib/contexts/MobileAppState';
import { useRouter } from 'next/router';
import { UserInfo } from '@/types/userInfo';

export interface StandaloneStateProvidersProps {
  device: Device;
  pageData: any;
  appState?: Partial<AppState>;
  mobileAppState?: Partial<MobileAppState>;
  paymentConfig?: PaymentConfig;
  userInfo?: UserInfo;
}

export const StandaloneStateProviders: Component<StandaloneStateProvidersProps> = ({ children, ...pageProps }) => {
  const { query, pathname } = useRouter();

  const { device, pageData, appState, mobileAppState, paymentConfig, userInfo } = useStable(pageProps);

  const { type, data, meta } = pageData ?? {};
  const { id, isNativeAd, accessLevel, purchaseTag, pageType: metaPageType } = meta ?? {};
  const { publishedUrl, category, tags } = data ?? {};

  const baseAppState: AppState = useStable({
    device,
    type,
    category,
    publishedUrl,
    tags,
    pageId: id,
    isNativeAd,
    accessLevel,
    purchaseTag,
    metaPageType,
    gamesUrls: ['/korsord', '/korsord-och-spel'],
    isPreviewPage: pathname.startsWith('/_preview'),
    isFrontPage: type === 'front',
    isArticlePage: type === 'article',
    isDesktop: device === 'desktop',
    isMobile: device === 'mobile',
    recommendedArticles: [],
    scrollArticles: [],
    paymentConfig,
    userInfo,
  });

  const baseMobileAppState: MobileAppState = useStable({
    isMobileApp: Boolean(query.allerapp),
    appVersion: query['app-version'] as string,
    buildVersion: query['build-version'] as string,
    environment: query.environment as MobileAppEnvironment,
    trackingStatus: query['tracking-status'] as MobileAppTrackingStatus,
  });

  return (
    <AppStateProvider {...baseAppState} {...appState}>
      <MobileAppStateProvider {...baseMobileAppState} {...mobileAppState}>
        <ComponentsStateProvider>{children}</ComponentsStateProvider>
      </MobileAppStateProvider>
    </AppStateProvider>
  );
};
